import {mdiStar, mdiStarOutline} from '@mdi/js';
import {MakesApiRequestsMixin} from "@spoferan/nuxt-spoferan/mixins";
import {ModelType} from "@spoferan/spoferan-ts-core";
import {useIndexStore} from "../store";
import {mapWritableState} from "pinia";

export default {

    mixins: [MakesApiRequestsMixin],

    emits: ['follow', 'unfollow'],

    props: {
        // The model to follow
        model: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            icons: {
                follow: mdiStarOutline,
                following: mdiStar,
            }
        }
    },

    computed: {
        isFollowing() {
            return this.findFollowIndex() > -1;
        },

        // The data of the model we require to follow
        // This is required to support following users when a member model is specified
        // This is null when the model can not be followed
        followModelData() {
            const isMemberModel = this.model.model_name === 'member';

            // We can only follow existing followable models
            if (isMemberModel && !this.model.user_id) {
                return null;
            }

            return {
                _key: this.model._key,
                id: isMemberModel ? this.model.user_id : this.model.id,
                model_name: isMemberModel ? 'user' : this.model.model_name,
                model_type: isMemberModel ? ModelType.User : this.model.model_type,
            };
        },

        ...mapWritableState(useIndexStore, ['follows']),
    },

    methods: {
        handleClick() {
            if (this.isFollowing) {
                this.unfollow();
            } else {
                this.follow();
            }
        },

        follow() {
            if (!this.followModelData) {
                return;
            }

            if (window.native_vibrate) {
                window.native_vibrate.postMessage('medium')
            }

            // We want to track it even for unauthenticated users
            this.$analytics.track({
                event: 'follow',
                model_type_name: this.followModelData.model_name,
                model_key: this.followModelData._key
            });

            if (!this.$auth.loggedIn) {
                this.$confirm({
                    title: this.$t('action.follow'),
                    text: this.$t('confirm.login_for_action.message'),
                    type: 'info',
                }).then(({confirmed}) => {
                    if (confirmed) {
                        this.$router.push({path: this.$web('auth.login'), query: {redirect: this.$route.fullPath, follow_type: this.followModelData.model_type, follow_id: this.followModelData.id}})
                    }
                });
                return;
            }

            this.makeApiRequest(this.getApiUrl(), {}, 'POST').then((response) => {
                const followIndex = this.findFollowIndex();
                if (followIndex < 0) {
                    this.follows.push(response.data);
                    this.$emit('follow');
                }
            });
        },

        unfollow() {
            if (!this.followModelData) {
                return;
            }

            if (window.native_vibrate) {
                window.native_vibrate.postMessage('light')
            }

            this.$analytics.track({
                event: 'unfollow',
                model_type_name: this.followModelData.model_name,
                model_key: this.followModelData._key
            });

            this.$confirm({
                title: this.$t('confirm.follower.destroy.title'),
                text: this.$t('confirm.follower.destroy.message'),
                type: 'warning',
            }).then(({confirmed}) => {
                if (confirmed) {
                    this.makeApiRequest(this.getApiUrl(), {}, 'DELETE').then(() => {
                        const followIndex = this.findFollowIndex();
                        if (followIndex > -1) {
                            this.follows.splice(followIndex, 1);
                            this.$emit('unfollow');
                        }
                    });
                }
            });
        },

        findFollowIndex() {
            return this.follows.findIndex(follow => follow.type === this.followModelData.model_type && follow.id === this.followModelData.id);
        },

        getApiUrl() {
            switch (this.followModelData.model_type) {
                case ModelType.User:
                    return `/v1/athletes/${this.followModelData.id}/followers`;
                case ModelType.Club:
                    return `/v1/clubs/${this.followModelData.id}/followers`;
                case ModelType.Event:
                    return `/v1/events/${this.followModelData.id}/followers`;
                default:
                    throw Error(`Unknown model type [${this.followModelData.model_type}] to follow.`);
            }
        }
    }
};

